import { useMemo } from 'react';
import { DateBucketValueType } from '@fabric-ui/components';
import { newDefaultTimeRange } from '../../../components/AvHistoricalDatePicker';
import { useAvContext } from '../../../context/AvContextProvider';
import { useCustomSearchParams } from '../../../hooks/UseCustomSearchParams2';
import { cleanEmptyFilters, filterToExpression } from '../../../utils/filterUtils';
import { getExpression } from '../../CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { DwQueryRequest, HistoricalFilterMode, SortBy, SortDir } from '../../CustomDashboards/types';
import { convertURIToValue } from '../reports.utils';

const getDims = (
  headCellsWithoutMeasurements: string[],
  expressions: Record<string, { function: string; bucket: string; alias: string }>
) =>
  headCellsWithoutMeasurements.map(id => ({
    name: id,
    ...(expressions[id] && {
      expression: getExpression(id, expressions[id].function, expressions[id].bucket as DateBucketValueType),
      alias: expressions[id].alias,
    }),
  }));

const getMetrics = (headCellsMeasurements: string[]) =>
  headCellsMeasurements.map(id => ({
    name: id,
    alias: id,
  }));

const getGroupByFields = (isCurrentTime: boolean, headCellsWithoutMeasurements: any[], headCellsMeasurements: any[]): string[] => {
  if (!isCurrentTime) {
    return headCellsWithoutMeasurements.map(({ name, alias }) => alias ?? name);
  }
  if (headCellsMeasurements.length && headCellsWithoutMeasurements.length) {
    return headCellsWithoutMeasurements.map(({ name, alias }) => alias ?? name);
  }
  return [];
};

interface UseDataQueryParams {
  headCellsWithoutMeasurements: string[];
  headCellsMeasurements: string[];
  expressions: Record<string, { function: string; bucket: string; alias: string }>;
  orderBy: Array<{ property: string; isAsc: boolean }>;
  typeMap: any;
  isCurrentTime: boolean;
  historicalFilterMode: HistoricalFilterMode;
  activeProjName: string;
  page: number;
  rowLimit: number;
}

export const useDataQuery = ({
  headCellsWithoutMeasurements,
  headCellsMeasurements,
  expressions,
  orderBy,
  typeMap,
  isCurrentTime,
  historicalFilterMode,
  activeProjName,
  page,
  rowLimit,
}: UseDataQueryParams): DwQueryRequest => {
  const {
    featureFlags,
    accountEntities: { aggProjs },
  } = useAvContext();

  const [filters] = useCustomSearchParams();

  const sorting: SortBy[] = useMemo(
    () =>
      orderBy.map(({ property, isAsc }) => ({
        name: expressions[property] ? expressions[property].alias : property,
        dir: isAsc ? SortDir.ASC : SortDir.DESC,
      })),
    [orderBy, expressions]
  );

  const getDataQueryFilters = useMemo(() => {
    const { historicalFilterMode: UNUSED_BECAUSE_NOT_PARSED, ...restFilters } = filters;
    const filter = cleanEmptyFilters(filterToExpression(typeMap, restFilters));

    if (isCurrentTime || historicalFilterMode === HistoricalFilterMode.CURRENT || !filter) {
      return filter;
    }
    if (featureFlags.WidgetHistoricalFilterMode && historicalFilterMode === HistoricalFilterMode.HISTORICAL) {
      return { isHistorical: true, ...filter };
    }
    return filter;
  }, [filters, typeMap, isCurrentTime, historicalFilterMode, featureFlags]);

  const dims = useMemo(() => getDims(headCellsWithoutMeasurements, expressions), [headCellsWithoutMeasurements, expressions]);
  const metrics = useMemo(() => getMetrics(headCellsMeasurements), [headCellsMeasurements]);

  return useMemo(
    () => ({
      timezoneOffset: new Date().getTimezoneOffset(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      select: {
        dims,
        metrics,
      },
      filter: getDataQueryFilters,
      top: {
        size: rowLimit,
        groupOthers: false,
        offset: page * rowLimit,
      },
      groupBy: getGroupByFields(isCurrentTime, dims, metrics),
      sorting,
      projectionId: aggProjs[activeProjName].projId,
      distinct: !headCellsMeasurements.length && !!headCellsWithoutMeasurements.length,
      ...(isCurrentTime
        ? {}
        : {
            timeRange: filters.timeRange ? convertURIToValue(filters.timeRange) : newDefaultTimeRange,
          }),
    }),
    [
      dims,
      metrics,
      getDataQueryFilters,
      rowLimit,
      page,
      isCurrentTime,
      sorting,
      aggProjs,
      activeProjName,
      headCellsMeasurements,
      headCellsWithoutMeasurements,
      filters.timeRange,
    ]
  );
};
